/**
 * Role-based access control utilities
 * Manages user roles and permissions
 */

// User roles
export const USER_ROLES = {
    FREE: 'Free',
    HOST: 'Host',
};

/**
 * Check if user is a Host (subscribed user)
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const isHostUser = (user) => {
    return user?.role === USER_ROLES.HOST;
};

/**
 * Check if user is Free (non-subscribed user)
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const isFreeUser = (user) => {
    return user?.role === USER_ROLES.FREE;
};

/**
 * Check if user has access to a feature
 * @param {Object} user - User object from UserContext
 * @param {string} feature - Feature name to check
 * @returns {boolean}
 */
export const hasFeatureAccess = (user, feature) => {
    const hostFeatures = [
        'no_ads',
        'no_service_fee',
        'enhanced_uploads',
        'stories',
        'blue_tick',
        'orange_glow',
        'sell_products',
    ];

    // Host users have access to all features
    if (isHostUser(user)) {
        return true;
    }

    // Free users don't have access to host-only features
    if (hostFeatures.includes(feature)) {
        return false;
    }

    // Default: allow access
    return true;
};

/**
 * Get user role display name
 * @param {Object} user - User object from UserContext
 * @returns {string}
 */
export const getUserRoleName = (user) => {
    return isHostUser(user) ? 'Host' : 'Free';
};

/**
 * Check if user can create a community
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const canCreateCommunity = (user) => {
    return !!user?.uid;
};

/**
 * Check if user can message a Host user
 * Free users may be restricted from messaging Host users
 * @param {Object} currentUser - Current user object
 * @param {Object} targetUser - Target user to message
 * @returns {boolean}
 */
export const canMessageUser = (currentUser, targetUser) => {
    // Host users can message anyone
    if (isHostUser(currentUser)) {
        return true;
    }

    // Free users can message other Free users
    if (isFreeUser(targetUser)) {
        return true;
    }

    // This is where you'd add logic for Free -> Host messaging restrictions
    // For now, return true (allow all messages)
    return true;
};

/**
 * Get subscription status badge color
 * @param {string} status - Subscription status
 * @returns {string} - Tailwind color classes
 */
export const getSubscriptionBadgeColor = (status) => {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 'trial':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        case 'past_due':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        case 'canceled':
        case 'canceling':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
};

/**
 * Format subscription status for display
 * @param {string} status - Subscription status
 * @returns {string}
 */
export const formatSubscriptionStatus = (status) => {
    if (!status) return 'No subscription';

    const statusMap = {
        active: 'Active',
        trial: 'Trial',
        past_due: 'Past Due',
        canceled: 'Canceled',
        canceling: 'Canceling',
        none: 'No subscription',
    };

    return statusMap[status] || status;
};

/**
 * Check if user can sell products
 * Only Host users can sell products in the store
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const canSellProducts = (user) => {
    return isHostUser(user);
};

/**
 * Fee Configuration
 * Centralized percentages for all transactions
 */
export const FEE_CONFIG = {
    PRODUCT_PURCHASE_SERVICE_FEE: 0.05, // 5% for Free Buyers
    PRODUCT_SALE_COMMISSION_FREE: 0.20, // 20% for Free Sellers
    PRODUCT_SALE_COMMISSION_PRO: 0.02,  // 2% for Pro Sellers
    JOB_SECURE_FEE_FREE: 0.15,          // 15% for Free Creators
    JOB_SECURE_FEE_PRO: 0.06,           // 6% for Pro Creators
};

/**
 * Calculate the service fee for a purchase
 * @param {Object} user - Current user (Buyer or Job-Seeker/Client)
 * @param {number} price - Base price
 * @param {string} type - 'product' or 'job'
 * @param {Object} creator - For jobs, the creator's data to check their role
 * @returns {Object} - { fee, total }
 */
export const calculatePurchaseFee = (user, price, type = 'product', creator = null) => {
    if (type === 'job') {
        // For jobs, the fee is based on the CREATOR'S role and deducted from creator payout.
        const isHostCreator = creator?.role === USER_ROLES.HOST || creator?.role === 'Pro';
        const feeRate = isHostCreator ? FEE_CONFIG.JOB_SECURE_FEE_PRO : FEE_CONFIG.JOB_SECURE_FEE_FREE;
        const fee = price * feeRate;
        const total = price;
        const creatorPayout = price - fee;

        return {
            fee: parseFloat(fee.toFixed(2)),
            total: parseFloat(total.toFixed(2)),
            rate: feeRate,
            creatorPayout: parseFloat(creatorPayout.toFixed(2)),
        };
    }

    // Default: Digital Product Purchase
    if (isHostUser(user)) {
        return {
            fee: 0,
            total: price,
            rate: 0
        };
    }

    // Free users pay 5% fee for products
    const fee = price * FEE_CONFIG.PRODUCT_PURCHASE_SERVICE_FEE;
    const total = price + fee;

    return {
        fee: parseFloat(fee.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        rate: FEE_CONFIG.PRODUCT_PURCHASE_SERVICE_FEE
    };
};

/**
 * Calculate the platform commission deducted from seller revenue
 * @param {Object} seller - Seller user object
 * @param {number} price - Base price of the product
 * @returns {Object} - { commission, settledAmount }
 */
export const calculateSellerCommission = (seller, price) => {
    const isFree = !seller || seller.role === USER_ROLES.FREE;
    const rate = isFree ? FEE_CONFIG.PRODUCT_SALE_COMMISSION_FREE : FEE_CONFIG.PRODUCT_SALE_COMMISSION_PRO;
    const commission = price * rate;
    const settledAmount = price - commission;

    return {
        rate,
        commission: parseFloat(commission.toFixed(2)),
        settledAmount: parseFloat(settledAmount.toFixed(2))
    };
};

